import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { softDeleteImage, useImages } from '@/lib/api/images';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Image } from '@/lib/api/images';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { columns } from './columns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
export function Images() {
  const [images] = useImages();
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [imageToDelete, setImageToDelete] = useState<Image | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!imageToDelete) return;

    setIsDeleting(true);
    try {
      await softDeleteImage(imageToDelete.id);
      toast.success('Image deleted');
      setImageToDelete(null);
    } catch (error) {
      toast.error('Failed to delete image');
    } finally {
      setIsDeleting(false);
    }
  };

  const table = useReactTable({
    data: (images || []) as any as Image[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    meta: { onDelete: setImageToDelete },
    // onColumnVisibilityChange: setColumnVisibility,
    // onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility: { tags: false },
      // columnFilters,
      // columnVisibility,
      // rowSelection,
    },
  });

  useEffect(() => {
    if (images?.length === 0) {
      setSelectedImageUrl(null);
      return;
    }

    if (
      !selectedImageUrl ||
      !images?.some((image) => image.url === selectedImageUrl)
    ) {
      setSelectedImageUrl(images?.[0]?.url || null);
    }
  }, [images, selectedImageUrl]);

  const selectedImage = useMemo(
    () => images?.find((image) => image.url === selectedImageUrl) || null,
    [images, selectedImageUrl]
  );

  return (
    <div className="w-full pb-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Images</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/images/create" className="w-fit">
            <Button type="button">New</Button>
          </Link>
        </div>
      </div>

      <Separator className="my-6" />

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card className="h-fit">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Images</CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <div className="border">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead
                            key={header.id}
                            className={
                              header.column.id === 'actions'
                                ? 'w-px'
                                : undefined
                            }
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        onClick={() => setSelectedImageUrl(row.original.url)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        No results.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-end space- pt-4 px-4">
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="h-fit">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedImage ? (
              <>
                <div className="overflow-hidden rounded-md border">
                  <img
                    src={selectedImage.url}
                    alt={selectedImage.name}
                    className="max-h-[340px] w-full object-contain"
                  />
                </div>
                <div className="space-y-2 text-sm">
                  <p className="font-medium break-all">{selectedImage.name}</p>
                </div>
              </>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Select an image to preview.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={!!imageToDelete}
        onOpenChange={(open) => {
          if (!open) setImageToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete image</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{imageToDelete?.name}"? It will
              no longer be visible in the list.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setImageToDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
